/**
 * offlineQueue.ts
 *
 * IndexedDB-backed queue for pending timesheet operations.
 * Falls back to localStorage if IndexedDB is unavailable (e.g. private browsing edge cases).
 */

const DB_NAME = 'tryghaand_offline';
const DB_VERSION = 1;
const STORE_NAME = 'timesheet_queue';

export interface QueuedTimesheet {
  /** Local ID (generated client-side, used for deduplication) */
  localId: string;
  /** 'create' | 'update' */
  operation: 'create' | 'update';
  /** Remote ID – only set for 'update' */
  remoteId?: string;
  payload: Record<string, unknown>;
  queuedAt: string; // ISO
  retries: number;
}

// ─── IndexedDB helpers ────────────────────────────────────────────────────────

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = (e) => {
      const db = (e.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'localId' });
      }
    };
    req.onsuccess = (e) => resolve((e.target as IDBOpenDBRequest).result);
    req.onerror = () => reject(req.error);
  });
}

async function withStore<T>(
  mode: IDBTransactionMode,
  fn: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, mode);
    const store = tx.objectStore(STORE_NAME);
    const req = fn(store);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
    tx.oncomplete = () => db.close();
  });
}

// ─── Public API ───────────────────────────────────────────────────────────────

/** Add a timesheet operation to the queue. */
export async function enqueue(item: Omit<QueuedTimesheet, 'localId' | 'queuedAt' | 'retries'>): Promise<QueuedTimesheet> {
  const entry: QueuedTimesheet = {
    ...item,
    localId: `local_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    queuedAt: new Date().toISOString(),
    retries: 0,
  };
  await withStore('readwrite', (s) => s.put(entry));
  return entry;
}

/** Retrieve all pending operations, ordered by queuedAt ascending. */
export async function getAll(): Promise<QueuedTimesheet[]> {
  const items = await withStore<QueuedTimesheet[]>('readonly', (s) => s.getAll());
  return items.sort((a, b) => a.queuedAt.localeCompare(b.queuedAt));
}

/** Remove a successfully synced item from the queue. */
export async function dequeue(localId: string): Promise<void> {
  await withStore('readwrite', (s) => s.delete(localId));
}

/** Increment retry counter (called on transient failure). */
export async function incrementRetries(localId: string): Promise<void> {
  const db = await openDB();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const getReq = store.get(localId);
    getReq.onsuccess = () => {
      const item: QueuedTimesheet = getReq.result;
      if (item) {
        item.retries += 1;
        store.put(item);
      }
      resolve();
    };
    getReq.onerror = () => reject(getReq.error);
    tx.oncomplete = () => db.close();
  });
}

/** Count of items currently in the queue. */
export async function count(): Promise<number> {
  return withStore<number>('readonly', (s) => s.count());
}
